import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button as ButtonAtom } from 'components/atoms/button';
import { Label } from 'components/atoms/label';
import { useTranslation } from 'hooks/use-translation';

enum DatePickerType {
  Range = 'range',
  Single = 'single',
}

const Button = styled(ButtonAtom)`
  border: 0;
  height: auto;
  padding: 0;

  &:hover {
    background: none !important;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: var(--spacing-04) var(--spacing-04) var(--spacing-02)
    var(--spacing-04);
`;

const PickTodayTomorrowWrapper = styled.div`
  margin-bottom: var(--spacing-03);
`;

const ModifyPickerTypeWrapper = styled.div`
  flex: 1;
  margin-bottom: var(--spacing-03);
  min-width: 240px;
`;

const DoneWrapper = styled.div`
  padding: var(--spacing-03);
  ${ButtonAtom} {
    width: 100%;
    justify-content: center;
  }
`;

const ClearDateWrapper = styled.div`
  margin-bottom: var(--spacing-03);
  flex: 1;
  display: flex;
  justify-content: flex-end;
`;

export const DueDatePickerFooter: FC<{
  footerText?: string;
  canChooseStartDate: boolean;
  useAsStartDatePicker?: boolean;
  disallowSingleDate?: boolean;
  hideModifyPicker?: boolean;
  hideClearDate?: boolean;
  type: 'range' | 'single';
  onPickToday(): void;
  onPickTomorrow(): void;
  onClear(): void;
  modifyPickerType(): void;
  onDone(): void;
}> = ({
  footerText,
  canChooseStartDate,
  useAsStartDatePicker,
  disallowSingleDate,
  hideModifyPicker = false,
  hideClearDate = false,
  type,
  onPickToday,
  onPickTomorrow,
  onClear,
  modifyPickerType,
  onDone,
}) => {
  const defaultFooterText = useTranslation('due_date.action');
  const finalFooterText = footerText ?? defaultFooterText;

  return (
    <>
      {!disallowSingleDate && !hideModifyPicker ? (
        <Wrapper>
          <Label>
            {useAsStartDatePicker ? (
              <FormattedMessage id="start_date.action" />
            ) : (
              <FormattedMessage id={finalFooterText} />
            )}
          </Label>
        </Wrapper>
      ) : null}

      <Wrapper>
        {!disallowSingleDate ? (
          <>
            <PickTodayTomorrowWrapper>
              <Button
                type="link"
                onClick={onPickToday}
                data-cy="due-date-picker.today"
              >
                <FormattedMessage id="today" />
              </Button>
              <span className="separator" />
              <Button
                type="link"
                onClick={onPickTomorrow}
                data-cy="due-date-picker.tomorrow"
              >
                <FormattedMessage id="tomorrow" />
              </Button>
            </PickTodayTomorrowWrapper>

            {hideModifyPicker ? null : (
              <ModifyPickerTypeWrapper>
                {canChooseStartDate &&
                  (type === DatePickerType.Single ? (
                    <Button
                      type="link"
                      onClick={modifyPickerType}
                      data-cy="due-date-picker.add-start-and-end-date"
                    >
                      <FormattedMessage id="due_date.range.action" />
                    </Button>
                  ) : (
                    <>
                      <span className="separator" />
                      <Button type="link" onClick={modifyPickerType}>
                        {useAsStartDatePicker ? (
                          <FormattedMessage id="start_date.action" />
                        ) : (
                          <FormattedMessage id={finalFooterText} />
                        )}
                      </Button>
                    </>
                  ))}
              </ModifyPickerTypeWrapper>
            )}
          </>
        ) : null}
        {hideModifyPicker || hideClearDate ? null : (
          <ClearDateWrapper>
            <Button
              danger
              type="link"
              onClick={onClear}
              data-cy="due-date-picker.clear-dates"
            >
              <FormattedMessage id="clear_dates" />
            </Button>
          </ClearDateWrapper>
        )}
      </Wrapper>
      <DoneWrapper>
        <ButtonAtom
          type="tertiary"
          onClick={() => onDone()}
          data-cy="due-date-picker.done"
        >
          <FormattedMessage id="done" />
        </ButtonAtom>
      </DoneWrapper>
    </>
  );
};
